import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Activity, AlertCircle, ArrowLeft, Building2, CheckCircle2, ChevronDown, ChevronRight, CreditCard, Eye, EyeOff, KeyRound, Link2, Mail, Pencil, Plus, Puzzle, Send, Trash2, Users as UsersIcon, X, XCircle } from 'lucide-react';
import uapiVendorsService, { UAPI_CREDENTIAL_SETTINGS } from '../services/uapiVendorsService';
// THE BINDING IS WRITTEN THROUGH THE SAME ENDPOINT THE CHAIN AND HOTEL SCREENS USE. A vendor binding
// differs from theirs in exactly one field — who presents the x-hotelid — so it is that endpoint with
// one more argument, not a second way to mint a routing identifier.
import uapiAccessService from '../services/UapiAccessService';
import entityMktSettingsService from '../services/partnerMktSettingsService';
import partnerService from '../services/partnerService';
import { userService } from '../services/userService';
import Button from './shared/Button';
import Card from './shared/Card';
import Badge from './shared/Badge';
import EmptyValue from './shared/EmptyValue';
import SearchSelect from './shared/SearchSelect';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import RedactedValue from './shared/RedactedValue';
import CopyButton from './shared/CopyButton';
import CredentialSelector from './shared/CredentialSelector';
import Tabs from './shared/Tabs';
import BillingTab from './BillingTab';
// OHIP TRANSACTIONS is the Admin > Billing screen itself, run scoped to this vendor (`vendorId`): the server
// narrows it to the OHIP hotel codes linked to the hotels the vendor was granted.
import OhipTransactionsPage from './OhipTransactionsPage';
// THE SAME CONSOLE THE LEGACY UNIVERSAL API SCREEN USES, not a second one. It already knows how to fill
// a path's parameters, mint a token on open, send the real request and explain what came back; the only
// thing this screen supplies differently is WHERE the bindings come from.
import UapiConsole from './shared/UapiConsole';
import { useAuth } from '../contexts/AuthContext';
import { BillingScheduleEditor, BillingScheduleView, scheduleText } from './BillingSchedule';
import './PaymentPorterPage.css'; // pp-toggle-btn — the shared toggle
import './OhipTransactionsPage.css';     // .otx-page / .otx-header / .otx-title — the admin page shell
import './OxiDequeueAdmin.css';         // .acp-table — the admin list table
import './OpenSearchAdmin.css';         // .os-msg-* — the inline message strip
import './TaskConfigurationPage.css';   // .empty-state / .loading-section
import './shared/Disclosure.css';       // .hc-disclosure — the clickable collapsed-section header
import './UniversalApiPage.css';        // .lu-method / .lu-mono — the method chips the console shares
import './VendorDetailPage.css';        // .vendor-title / .vendor-rename — renaming in place

// ONE VENDOR: what it may use to reach us, and who it is.
//
// A PAGE, NOT A DIALOG (STYLE.md "No Modals"), so it has a URL to send to a colleague and a back button
// that works — and because two tabs of detail is not a thing to put behind an overlay.
//
// TWO TABS, AND THE ORDER IS THE POINT. Credentials first: a vendor that cannot authenticate is not
// integrated, whatever else is true of it. Users second: the profiles under the partner that represent
// this company, which is how it shows up in an access check.
//
// CREDENTIALS ARE ISSUED HERE, NOT WHEN THE VENDOR IS CREATED. Minting at creation meant the one moment
// the client secret is visible arrived before anybody had decided the vendor was set up correctly, and
// any failure after it left a live secret shown once and recorded nowhere. Now somebody presses the
// button when they are ready to hand it over.

// A CREDENTIAL'S SETTINGS, FOLDED AWAY UNTIL ASKED FOR.
//
// Seven rows of tokens and urls is most of the page, and none of it is what somebody usually opens a
// vendor to see — they come for "can it authenticate" and "what can it reach". So it collapses, and it
// starts collapsed: secrets that are not on screen cannot be read over a shoulder or caught by a
// screenshot, and the one press to open them is a deliberate act.
//
// ONE COMPONENT FOR BOTH TABLES. The Universal API settings and the Other settings were already two
// near-identical copies of the same markup; a second copy of a chevron and a reveal control on top of
// that is how they start to differ.
//
// Chevron shape and behaviour follow the house pattern (OnsiteInstructionsPage): a real <button> with
// aria-expanded, and the chevron rotates rather than swapping to a second icon.
const SettingsTable = ({ title, hint, rows, nameOf, showAll, onToggleShowAll, startOpen = false }) => {
    const [open, setOpen] = useState(startOpen);

    // CLOSING RE-HIDES THE VALUES. Otherwise reopening the section would show every secret in full,
    // because "show all" was still on from an hour ago — a state nobody would expect to persist behind
    // a closed panel.
    const toggleOpen = () => {
        setOpen((o) => {
            if (o && showAll) onToggleShowAll(false);
            return !o;
        });
    };

    return (
        <>
            <button
                type="button"
                className="hc-disclosure"
                onClick={toggleOpen}
                aria-expanded={open}
            >
                <span className="hc-disclosure-title">{title}</span>
                <span className="hc-disclosure-count">
                    {rows.length} setting{rows.length === 1 ? '' : 's'}
                </span>
                <span className="hc-disclosure-action">
                    {open ? 'Hide' : 'Show'}
                    <ChevronDown size={16} className="hc-disclosure-chevron" />
                </span>
            </button>

            {hint && open && <p className="form-hint" style={{ margin: '0.5rem 0 0' }}>{hint}</p>}

            {open && (
                <table className="acp-table hc-disclosure-panel">
                    <thead>
                        <tr>
                            <th>Setting</th>
                            <th>
                                {/* On the column it governs, rather than beside the actions above —
                                    those act on the credential, this only changes what is on screen. */}
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                                    Value
                                    <button
                                        type="button"
                                        onClick={() => onToggleShowAll(!showAll)}
                                        title={showAll ? 'Hide all values' : 'Show all values'}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                                                 background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                                 color: '#4a6cf7', font: 'inherit', textTransform: 'none',
                                                 letterSpacing: 'normal' }}
                                    >
                                        {showAll ? <EyeOff size={14} /> : <Eye size={14} />}
                                        {showAll ? 'Hide all' : 'Show all'}
                                    </button>
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(s => (
                            <tr key={nameOf(s)}>
                                <td style={{ fontWeight: 600 }}>{nameOf(s)}</td>
                                <td>
                                    {/* RedactedValue is the shared control for a stored secret — masked,
                                        revealable, copyable — so a vendor's credential behaves like
                                        every other credential on the site. */}
                                    <RedactedValue value={s.setting_value ?? s.value} reveal={showAll} />
                                    <CopyButton value={s.setting_value ?? s.value} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </>
    );
};

// VENDOR SETTINGS IS THIS PAGE, not a second one. A person who works at a vendor opens it from the sidebar
// (Vendor Settings, shown while the partner they are a vendor for is chosen) with `vendorId` = their own
// vendor and `selfView`: READ-ONLY, and without the tabs that work through the partner's credentials
// (Credentials, Test) or the partner's billing. What the vendor may call, where, and who works there is
// what they come to see; changing any of it stays the partner admin's — the lambda refuses every write
// from them, so the buttons are not offered (uapivendors/index.js, "A PERSON AT A VENDOR").
// No Modules: which Universal API modules a vendor may call is the partner's decision, not something its people
// need to read on every visit.
const SELF_TABS = ['access', 'transactions', 'users'];

// A vendor grant's level (uapi_vendor_access, V624). None: the vendor works with the hotel (its page, OHIP
// Transactions, billing, the Universal API) but its people have no permission on it. User / Admin: they do.
const LEVEL_LABEL = { N: 'None', U: 'User', A: 'Admin' };

// SEVERAL HOTELS OF A CHAIN ON ONE ROW, EACH AT ITS OWN LEVEL. The server keeps one grant per chain or per hotel
// (uapi_vendor_access), each with its level; the screen shows a chain's hotel grants as ONE row with a multi-select,
// and the hotels under it with a level each. groupGrants turns the server's list into rows ({ accountId — the whole
// chain, or null; chainId — the chain the row is about; hotels: [{ hotelId, permissionType }]; permissionType — the
// whole chain's level }); expandGrants turns rows back into the server's.
const groupGrants = (list, hotels) => {
    const chainOf = (hid) => (hotels.find(h => String(h.id) === String(hid)) || {}).account_id || null;
    const rows = [];
    const byChain = {};
    for (const g of list) {
        if (!g.hotelId) {
            rows.push({ accountId: g.accountId, chainId: g.accountId, hotels: [], permissionType: g.permissionType });
            continue;
        }
        const chainId = chainOf(g.hotelId);
        if (!byChain[chainId]) {
            byChain[chainId] = { accountId: null, chainId, hotels: [], permissionType: 'N' };
            rows.push(byChain[chainId]);
        }
        byChain[chainId].hotels.push({ hotelId: g.hotelId, permissionType: g.permissionType });
    }
    return rows;
};
const idsOf = (g) => (g.hotels || []).map(h => h.hotelId);
const expandGrants = (rows) => rows.flatMap(g => ((g.hotels || []).length
    ? g.hotels.map(h => ({ accountId: null, hotelId: h.hotelId, permissionType: h.permissionType }))
    : g.accountId ? [{ accountId: g.accountId, hotelId: null, permissionType: g.permissionType }] : []));

const VendorDetailPage = ({ vendorId = null, selfView = false }) => {
    const navigate = useNavigate();
    const params = useParams();
    const id = vendorId != null ? String(vendorId) : params.id;
    const readOnly = selfView;
    // Only for the two admin-only panels inside the console — never for what this screen shows or
    // writes, which the lambda authorises from the caller's own partner.
    const { isSuperAdmin, vendorOf, user: me, partners: myPartners } = useAuth();
    // Inviting the vendor's people: the partner admin's view always; in Vendor Settings, the vendor's own admins
    // (users.uapi_vendor_role 'A'). The server enforces both — partners/index.js lets a vendor admin invite into
    // their own vendor only.
    const canInvite = !selfView || !!(vendorOf && vendorOf.isAdmin && Number(vendorOf.credentialId) === Number(id));
    const [searchParams, setSearchParams] = useSearchParams();
    const from = searchParams.get('from');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Renaming the vendor in place (the partner admin's view only — never selfView).
    const [renaming, setRenaming] = useState(false);
    const [nameDraft, setNameDraft] = useState('');
    const [savingName, setSavingName] = useState(false);
    const [nameError, setNameError] = useState(null);
    const [vendor, setVendor] = useState(null);
    const [users, setUsers] = useState([]);
    const [credential, setCredential] = useState(null);   // the entity_credentials row + its settings
    // THE TAB IS IN THE URL. A tab that lives only in component state cannot be linked to, does not
    // survive a refresh, and is lost by Back — so "look at this vendor's users" is not something one
    // person can send another. Read on arrival, written on every click.
    const TABS = selfView ? SELF_TABS : ['credentials', 'modules', 'test', 'access', 'transactions', 'users', 'billing'];
    const [tab, setTab] = useState(() => {
        const t = searchParams.get('tab');
        return TABS.includes(t) ? t : TABS[0];
    });

    // REPLACE, NOT PUSH: clicking between two tabs while reading a vendor would otherwise stack history
    // entries to reverse before Back leaves the screen. `from` is carried through so the Back button
    // still knows where it came from.
    const changeTab = (next) => {
        setTab(next);
        const params = { tab: next };
        if (from) params.from = from;
        setSearchParams(params, { replace: true });
    };

    // ── Modules ──────────────────────────────────────────────────────────────────────────────────
    // `granted` is the working set of route ids the screen edits; `routes` is what may be granted;
    // `lockedPaths` is what the server refuses to revoke (/oauth — without it the vendor cannot log in).
    const [routes, setRoutes] = useState([]);
    const [granted, setGranted] = useState(new Set());
    const [lockedPaths, setLockedPaths] = useState([]);
    const [modulesLoaded, setModulesLoaded] = useState(false);
    const [savingModules, setSavingModules] = useState(false);
    const [modulesMsg, setModulesMsg] = useState(null);

    // ── Test ─────────────────────────────────────────────────────────────────────────────────────
    // What this vendor can actually call, from the server: the operations of the modules granted to it
    // and the property bindings issued to its credential. Both are the GATEWAY'S rules, read rather
    // than re-derived here — a screen that decided for itself what a grant covers would eventually
    // offer a call the dispatcher refuses.
    const [testOps, setTestOps] = useState([]);
    const [testBindings, setTestBindings] = useState([]);
    const [testModules, setTestModules] = useState([]);
    const [testLoaded, setTestLoaded] = useState(false);
    const [testError, setTestError] = useState(null);
    // The one call the console is open on. Null closes it.
    const [testOperation, setTestOperation] = useState(null);
    // THE PROPERTY IS CHOSEN ONCE FOR THE TAB, not per call. Somebody testing a vendor works down the
    // list against one property; being asked which binding inside every call was a question with the
    // same answer every time.
    const [testXHotelId, setTestXHotelId] = useState('');
    // The live token, in component state ONLY — never localStorage. A bearer token written to disk
    // outlives the tab it was minted in and is readable by anything served from this origin.
    const [token, setToken] = useState(null);

    // ── Chains / Hotels ──────────────────────────────────────────────────────────────────────────
    // `grants` is the working copy the screen edits; it is sent back whole. `chains`/`hotels` are what
    // may be granted — this vendor's partner's, and nothing else.
    const [grants, setGrants] = useState([]);
    // The set as the server last had it — sent with a save so one made over somebody else's change is refused.
    const [loadedGrants, setLoadedGrants] = useState(null);
    const grantCount = expandGrants(grants).length;   // what the server holds: one per chain or hotel
    // A whole-chain row about to be narrowed to one hotel — confirmed first (HIL-126).
    const [narrowing, setNarrowing] = useState(null);   // { index, hotelId }
    const [chains, setChains] = useState([]);
    const [hotels, setHotels] = useState([]);
    const [accessLoaded, setAccessLoaded] = useState(false);
    const [savingAccess, setSavingAccess] = useState(false);
    const [accessMsg, setAccessMsg] = useState(null);

    // ── Universal API routing ────────────────────────────────────────────────────────────────────
    // A binding is (property → backend system → the credential we call it with), named by an x-hotelid
    // that THIS vendor presents. `bindings` comes back with the vendor; `systems` is the backends
    // switched on for the Universal API.
    const [bindings, setBindings] = useState([]);
    const [systems, setSystems] = useState([]);
    const [addingBinding, setAddingBinding] = useState(false);
    const [bindForm, setBindForm] = useState({ hotelId: null, marketplaceId: '', credential: null, hotelidOverride: '', label: '' });
    const [savingBinding, setSavingBinding] = useState(false);
    const [bindingMsg, setBindingMsg] = useState(null);
    const [confirmRemoveBinding, setConfirmRemoveBinding] = useState(null);

    // ── Adding somebody who works at the vendor ──────────────────────────────────────────────────
    const [addingUser, setAddingUser] = useState(false);
    const [userForm, setUserForm] = useState({ email: '', firstname: '', lastname: '', role: 'PARTNER_USER' });
    const [savingUser, setSavingUser] = useState(false);
    const [userError, setUserError] = useState(null);
    const [userResent, setUserResent] = useState(null);

    // REVEAL EVERYTHING AT ONCE. Seven settings behind seven eyes is seven clicks to read a credential
    // out to somebody, and the values are revealable one at a time already — this is convenience, not a
    // new exposure. Each row's own eye still works afterwards (see RedactedValue's `reveal`), so one
    // value can be hidden again while screen-sharing without collapsing the rest.
    //
    // NOT PERSISTED, deliberately: it resets on every visit, so a page left open does not come back
    // showing a client secret.
    const [showAllSecrets, setShowAllSecrets] = useState(false);

    // Testing the credentials we issued — { success, message } or null.
    const [authResult, setAuthResult] = useState(null);
    const [authRunning, setAuthRunning] = useState(false);

    const [issuing, setIssuing] = useState(false);
    const [confirmIssue, setConfirmIssue] = useState(false);
    const [issued, setIssued] = useState(null);           // shown ONCE, then gone
    const [actionError, setActionError] = useState(null);

    // Back to where the vendor was opened from. Partner Settings is its own page with a tab, so the tab
    // is named rather than guessed — landing on Details after editing a vendor reads as having lost it.
    const backTo = from === 'partner-settings' ? '/partner-settings?tab=vendors' : '/vendors';

    // ── WHAT THIS CREDENTIAL HOLDS ───────────────────────────────────────────────────────────────
    //
    // DERIVED ABOVE THE HOOKS, not beside the markup that renders it. `hasUapi` is read by the
    // sign-in effect below, and a `const` is in its temporal dead zone until its own line runs — with
    // these sitting further down the component, the first render threw
    // "Cannot access 'hasUapi' before initialization" before anything appeared.
    const settings = credential?.settings || [];
    const nameOf = (s) => String(s.setting_name || s.name || '').toUpperCase();
    // THE CREDENTIAL IS SPLIT BY WHAT PUT THE VALUES THERE. The Universal API section holds what
    // generating issues — the client id, secret, token url and scope the vendor authenticates to US
    // with. Anything else the marketplace defines is somebody's own entry and is shown separately
    // rather than swept into a section that offers to regenerate it.
    const uapiSettings = settings.filter(s => UAPI_CREDENTIAL_SETTINGS.includes(nameOf(s)));
    const otherSettings = settings.filter(s => !UAPI_CREDENTIAL_SETTINGS.includes(nameOf(s)));
    const hasUapi = uapiSettings.length > 0;

    // THE ADDRESS THE VENDOR ACTUALLY CALLS. The generated settings say who it is (client id, secret) and
    // where to get a token (TOKENURL, Cognito) — but not where to send the request, which is the one
    // thing it cannot work out for itself. From the build-time env, as UniversalApiPage reads it: there
    // is one gateway per environment at a domain we own, so it is shown to be copied, never chosen.
    const gatewayUrl = process.env.REACT_APP_UAPI_URL || '';

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await uapiVendorsService.get(id);
            const v = res?.vendor || null;
            setVendor(v);
            setUsers(Array.isArray(res?.users) ? res.users : []);
            setBindings(Array.isArray(res?.bindings) ? res.bindings : []);

            // THE CREDENTIAL COMES FROM THE CREDENTIAL SCREENS' OWN ENDPOINT, not from a copy of the
            // settings bolted onto this one. listCredentials is what the Credentials tab everywhere else
            // reads, so what a vendor's credential looks like here is what it looks like there — one
            // definition of "a credential and its settings", decrypted and redacted the same way.
            // Not in selfView: this lists EVERY credential the partner holds, which is not the vendor's to read.
            if (v?.partner_id && !selfView) {
                const credRes = await entityMktSettingsService.listCredentials('partner', v.partner_id);
                const all = credRes?.credentials || credRes || [];
                setCredential((Array.isArray(all) ? all : []).find(c => String(c.credential_id) === String(id)) || null);
            }
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Could not load this vendor');
        } finally {
            setLoading(false);
        }
    }, [id, selfView]);

    useEffect(() => { load(); }, [load]);

    // FETCHED WITH THE PAGE, not when the tab is first opened. It was deferred to save a query, but the tab
    // bar shows the grant count, and a count that only appears once the tab has been clicked reads as "none"
    // on every other tab (HIL-131). One query, and the Users tab's "they inherit N grants" needs it too.
    useEffect(() => {
        if (accessLoaded) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await uapiVendorsService.getAccess(id);
                if (cancelled) return;
                const loaded = (res?.grants || []).map(g => ({
                    accountId: g.account_id || null,
                    hotelId: g.hotel_id || null,
                    // Three levels (V624): None unless the grant says User or Admin.
                    permissionType: ['A', 'U'].includes(g.permission_type) ? g.permission_type : 'N',
                }));
                setGrants(groupGrants(loaded, res?.hotels || []));
                setLoadedGrants(loaded);
                setChains(res?.chains || []);
                setHotels(res?.hotels || []);
                setAccessLoaded(true);
            } catch (e) {
                if (!cancelled) setAccessMsg({ error: e.response?.data?.message || e.message || 'Could not load access' });
            }
        })();
        return () => { cancelled = true; };
    }, [accessLoaded, id]);

    // Fetched when the tab is first opened, for the same reason the access set is: a vendor is usually
    // opened to look at its credentials.
    useEffect(() => {
        if (tab !== 'modules' || modulesLoaded) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await uapiVendorsService.getModules(id);
                if (cancelled) return;
                setRoutes(res?.routes || []);
                setLockedPaths(res?.lockedPaths || []);
                setGranted(new Set((res?.modules || []).map(m => Number(m.uapi_route_id))));
                setModulesLoaded(true);
            } catch (e) {
                if (!cancelled) setModulesMsg({ error: e.response?.data?.message || e.message || 'Could not load modules' });
            }
        })();
        return () => { cancelled = true; };
    }, [tab, modulesLoaded, id]);

    // Fetched when the Test tab is first opened, like the other two: most visits to a vendor are about
    // its credentials, and this is two joins and a recursive walk on the server.
    useEffect(() => {
        if (tab !== 'test' || testLoaded) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await uapiVendorsService.getTestSurface(id);
                if (cancelled) return;
                setTestOps(res?.operations || []);
                setTestBindings(res?.bindings || []);
                setTestModules(res?.modules || []);
                setTestLoaded(true);
            } catch (e) {
                if (!cancelled) setTestError(e.response?.data?.message || e.message || 'Could not load what this vendor can call');
            }
        })();
        return () => { cancelled = true; };
    }, [tab, testLoaded, id]);

    // SAVING MODULES INVALIDATES THE TEST SURFACE. Ticking /rsv and then opening Test must offer /rsv's
    // calls — without this the tab keeps the set it loaded before the change and quietly tests the old
    // grants. Cheap: it only forces the next VISIT to the tab to re-read.
    useEffect(() => { setTestLoaded(false); }, [granted]);

    // SIGNING IN IS NOT A STEP SOMEBODY SHOULD HAVE TO TAKE. Every call from this tab is made as the
    // vendor, the page already holds the credential and the function that mints a token, and there is
    // nothing in that exchange for a person to decide — so it happens on arrival. The only time
    // authentication is mentioned is when it FAILED, which is a real finding about the credential.
    useEffect(() => {
        if (tab !== 'test' || !hasUapi) return;
        if (token && token.expiresAt > Date.now()) return;
        if (authRunning) return;
        testAuth();
        // DEPS ARE DELIBERATE: testAuth is a useCallback over [id, token], and `authRunning` is read
        // rather than depended on, so a failed attempt does not re-fire in a loop — it is retried by
        // leaving and reopening the tab, or with the Retry button.
    }, [tab, hasUapi, token]);

    // One binding is the common case, and choosing it removes a question with exactly one answer.
    useEffect(() => {
        if (testXHotelId || testBindings.length === 0) return;
        if (testBindings.length === 1) setTestXHotelId(testBindings[0].uapi_hotel_id || '');
    }, [testBindings, testXHotelId]);

    const selectedBinding = testBindings.find(b => b.uapi_hotel_id === testXHotelId) || null;

    // MEMOISED, AND IT MATTERS. The console seeds its own state from this prop and its loader depends
    // on it, so a fresh `[selectedBinding]` on every render would re-seed, re-render and loop forever.
    // `find` hands back the same object out of `testBindings`, so both deps are stable between renders.
    const consoleBindings = useMemo(
        () => (selectedBinding ? [selectedBinding] : testBindings),
        [selectedBinding, testBindings]);

    const isLocked = (route) => lockedPaths.includes(route.path);

    const toggleModule = (routeId) => setGranted((g) => {
        const next = new Set(g);
        if (next.has(routeId)) next.delete(routeId); else next.add(routeId);
        return next;
    });

    const saveModules = async () => {
        setSavingModules(true);
        setModulesMsg(null);
        try {
            // The locked path is left out of what is SENT — the server grants it regardless, and
            // including it would make the screen look like it decides something it does not.
            const ids = routes.filter(r => granted.has(Number(r.id)) && !isLocked(r)).map(r => Number(r.id));
            const res = await uapiVendorsService.saveModules(id, ids);
            setGranted(new Set((res?.modules || []).map(m => Number(m.uapi_route_id))));
            setModulesMsg({ ok: res?.message || 'Saved' });
        } catch (e) {
            setModulesMsg({ error: e.response?.data?.message || e.message || 'Could not save modules' });
        } finally {
            setSavingModules(false);
        }
    };

    // The backends switched on for the Universal API, fetched with the tab that needs them.
    useEffect(() => {
        if (tab !== 'access' || systems.length) return;
        uapiAccessService.systems()
            .then(r => setSystems((r?.data || r)?.systems || []))
            .catch(() => setSystems([]));
    }, [tab, systems.length]);

    // WHICH PROPERTIES MAY BE ROUTED — the ones this vendor has been granted, directly or through a
    // chain. The same rule the server applies (`vendorCoversHotel`), so the picker cannot offer a
    // property the save would refuse. Derived from the grants as EDITED: a grant added but not yet
    // saved is not a grant, which the hint below the picker says rather than leaving to a 400.
    const grantedHotels = (() => {
        const hotelIds = new Set(grants.flatMap(idsOf).map(String));
        const accountIds = new Set(grants.filter(g => g.accountId).map(g => String(g.accountId)));
        return hotels.filter(h => hotelIds.has(String(h.id)) || accountIds.has(String(h.account_id)));
    })();

    const openAddBinding = () => {
        setBindForm({ hotelId: null, marketplaceId: '', credential: null, hotelidOverride: '', label: '' });
        setBindingMsg(null);
        setAddingBinding(true);
    };

    const saveBinding = async () => {
        if (!bindForm.hotelId) { setBindingMsg({ error: 'Choose the property this routes to' }); return; }
        if (!bindForm.marketplaceId) { setBindingMsg({ error: 'Choose the backend system' }); return; }
        if (!bindForm.credential) { setBindingMsg({ error: 'Choose the credential the Universal API should call that system with' }); return; }
        setSavingBinding(true);
        setBindingMsg(null);
        try {
            const res = await uapiAccessService.create({
                hotelId: Number(bindForm.hotelId),
                marketplaceId: Number(bindForm.marketplaceId),
                credentialId: bindForm.credential.credential_id || bindForm.credential.id,
                // WHAT MAKES THIS A VENDOR BINDING. Without it the identifier would be minted for the
                // property's own credential and this vendor could not present it.
                uapiCredentialId: Number(id),
                hotelidOverride: bindForm.hotelidOverride.trim() || null,
                label: bindForm.label.trim() || null,
            });
            const created = (res?.data || res);
            setAddingBinding(false);
            setBindingMsg({ ok: `Issued ${created?.uapi_hotel_id || 'a routing identifier'}` });
            await load();
        } catch (e) {
            setBindingMsg({ error: e.response?.data?.message || e.message || 'Could not create that routing' });
        } finally {
            setSavingBinding(false);
        }
    };

    const removeBinding = async () => {
        const row = confirmRemoveBinding;
        setConfirmRemoveBinding(null);
        try {
            await uapiAccessService.remove(row.id);
            setBindingMsg({ ok: `${row.uapi_hotel_id} retired` });
            await load();
        } catch (e) {
            setBindingMsg({ error: e.response?.data?.message || e.message || 'Could not remove that routing' });
        }
    };

    const chainItems = chains.map(c => ({ id: c.id, label: c.name }));
    const hotelItems = hotels.map(h => ({ id: h.id, label: h.name, code: h.account_name, accountId: h.account_id }));
    // THE CHAIN A ROW IS ABOUT: its own, or — for a single-hotel grant — the hotel's. A hotel row showed "—" for its
    // chain, so nothing said Likuliku Lagoon was Ahura's (HIL-126).
    const chainOfRow = (g) => g.accountId || g.chainId
        || (hotels.find(h => String(h.id) === String(idsOf(g)[0])) || {}).account_id || null;
    const wholeChain = (g) => !!g.accountId && !idsOf(g).length;
    // The row's chain NAME: from the chain list, else from its hotel (a single-hotel grant's chain is not always listed).
    const chainNameOf = (g) => {
        const cid = chainOfRow(g);
        return chainItems.find(c => String(c.id) === String(cid))?.label
            || (hotels.find(h => String(h.account_id) === String(cid)) || {}).account_name || null;
    };
    const hotelName = (hid) => hotelItems.find(h => String(h.id) === String(hid))?.label || `Hotel ${hid}`;
    const rowHotelNames = (g) => idsOf(g).map(hotelName);
    // One hotel: its billing on the row. Several: a summary, as a chain's — each hotel's row (opened) carries its own.
    const rowBilling = (g) => (idsOf(g).length === 1 ? billingCell(hotelById(idsOf(g)[0]))
        : idsOf(g).length ? billingSummary(idsOf(g).map(hotelById).filter(Boolean))
        : g.accountId ? chainBillingSummary(g.accountId) : null);
    const levelSelect = (value, onChange, label) => (
        <select className="form-input" value={value} onChange={(e) => onChange(e.target.value)} style={{ minWidth: '8rem' }}
                aria-label={label}>
            <option value="N">None</option>
            <option value="U">User</option>
            <option value="A">Admin</option>
        </select>
    );
    // THE LEVEL, PER HOTEL WHEN HOTELS ARE CHOSEN: the whole chain has one; one hotel shows its own on the row; several
    // say so and list each hotel beneath with its level and billing (and, while editing, a way to drop just that one).
    const setHotelLevel = (i, hotelId, permissionType) => setGrants(gs => gs.map((row, n) => (n !== i ? row
        : { ...row, hotels: row.hotels.map(h => (String(h.hotelId) === String(hotelId) ? { ...h, permissionType } : h)) })));
    const rowLevel = (g, i, editable) => {
        const hs = g.hotels || [];
        if (hs.length > 1) return <span className="form-hint" style={{ margin: 0 }}>Per hotel</span>;
        if (hs.length === 1) {
            return editable ? levelSelect(hs[0].permissionType, (v) => setHotelLevel(i, hs[0].hotelId, v), `Level at ${hotelName(hs[0].hotelId)}`)
                : (LEVEL_LABEL[hs[0].permissionType] || 'None');
        }
        return editable ? levelSelect(g.permissionType, (v) => setGrant(i, { permissionType: v }), 'Level')
            : (LEVEL_LABEL[g.permissionType] || 'None');
    };
    const hotelLevelRows = (g, i, editable) => ((g.hotels || []).length > 1 && isOpen(g, i) ? g.hotels.map(h => [
        <tr key={`${i}-lvl${h.hotelId}`} style={{ background: '#f9fafb' }}>
            <td />
            <td style={{ paddingLeft: '1.25rem' }}>{hotelName(h.hotelId)}</td>
            <td>{editable ? levelSelect(h.permissionType, (v) => setHotelLevel(i, h.hotelId, v), `Level at ${hotelName(h.hotelId)}`)
                : (LEVEL_LABEL[h.permissionType] || 'None')}</td>
            <td>{billingCell(hotelById(h.hotelId))}</td>
            <td style={{ textAlign: 'right' }}>
                {editable && (
                    <button type="button" title={`Remove ${hotelName(h.hotelId)}`} aria-label={`Remove ${hotelName(h.hotelId)}`}
                            onClick={() => pickHotels(i, idsOf(g).filter(x => String(x) !== String(h.hotelId)))}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '0.25rem' }}>
                        <X size={14} />
                    </button>
                )}
            </td>
        </tr>,
        scheduleRow(hotelById(h.hotelId), `${i}-lvl${h.hotelId}`),
    ]) : null);
    const hotelsOfChain = (accountId) => hotels.filter(h => String(h.account_id) === String(accountId));
    // WHICH HOTELS THE VENDOR PAYS FOR, AND WHEN (hotel.bill_to + bill_to_credential_id V564, dated by V625's
    // hotel_bill_to_periods — e.g. direct to the hotel until 31 Mar 2026, through the vendor from 1 Apr 2026). Set here
    // by the vendor's PARTNER — an admin of it, or a superadmin — in the shared schedule editor, through the same
    // billing endpoint as the hotel's own Billing tab, so its checks apply: a payer whose period has not ended must have
    // complete billing details. Everybody else sees the schedule, read-only.
    const canBill = isSuperAdmin || (myPartners || []).some(p => p.isAdmin && String(p.partnerId) === String(vendor && vendor.partner_id));
    const [openChains, setOpenChains] = useState({});       // chain id -> expanded
    const [openSchedules, setOpenSchedules] = useState({});   // hotel id -> its billing dates open under its row
    const toggleSchedule = (hid) => setOpenSchedules(o => ({ ...o, [hid]: !o[hid] }));
    // A ROW FOLDS when it has hotels under it — a whole chain (its hotels) or several hotels picked (each with its level):
    // the same chevron for both. Keyed by chain for a whole chain, by row for a list. Closed by default, except a list
    // being edited, whose levels are set below it.
    const foldable = (g) => wholeChain(g) || (g.hotels || []).length > 1;
    const foldKey = (g, i) => (wholeChain(g) ? String(g.accountId) : `row-${i}`);
    const isOpen = (g, i) => openChains[foldKey(g, i)] ?? (!readOnly && !wholeChain(g));
    const toggleRow = (g, i) => setOpenChains(o => ({ ...o, [foldKey(g, i)]: !isOpen(g, i) }));
    // Today's payer — hotel.bill_to is always the payer of the period covering today.
    const billedHere = (h) => h && h.bill_to === 'VENDOR' && String(h.bill_to_credential_id) === String(id);
    const payerLabel = (p) => (p.billTo === 'VENDOR'
        ? (String(p.billToCredentialId) === String(id) ? 'Through vendor' : 'Through another vendor')
        : p.billTo === 'CHAIN' ? 'Chain pays' : p.billTo === 'PARTNER' ? 'Partner pays' : 'Direct to hotel');
    const periodsOfHotel = (h) => (h.bill_periods && h.bill_periods.length
        ? h.bill_periods
        : [{ from: null, to: null, billTo: h.bill_to || 'HOTEL', billToCredentialId: h.bill_to_credential_id }]);
    const scheduleSaved = (h, res) => {
        setHotels(list => list.map(x => (x.id === h.id
            ? { ...x, bill_to: res.billTo, bill_to_credential_id: res.billToCredentialId || null, bill_periods: res.periods || [] }
            : x)));
        setAccessMsg({ ok: `${h.name}: ${scheduleText(res.periods || [], payerLabel)}` });
    };
    // EACH HOTEL'S BILLING: its schedule in a line, and a chevron that opens the dates IN PLACE, under the row
    // (scheduleRow) — the editor for whoever may bill (canBill), the same windows read-only for everyone else.
    const billingCell = (h) => {
        if (!h) return <EmptyValue />;
        const text = scheduleText(periodsOfHotel(h), payerLabel);
        return (
            <span className="bsch-cell">
                <button type="button" className="hc-chevron-btn" onClick={() => { setAccessMsg(null); toggleSchedule(h.id); }}
                    aria-expanded={!!openSchedules[h.id]}
                    aria-label={openSchedules[h.id] ? `Hide the billing dates of ${h.name}` : `Show the billing dates of ${h.name}`}
                    title={canBill ? `Who pays for ${h.name}, and from when — direct to the hotel or through ${vendor.name}`
                        : `Who is billed for ${h.name}, and in which dates — set by the partner`}>
                    <ChevronRight size={14} strokeWidth={2.5} />
                </button>
                <span className="form-hint" style={{ margin: 0 }}>{text}</span>
            </span>
        );
    };
    // The opened dates: a row of its own under the hotel's, across the Hotel → Actions columns.
    const scheduleRow = (h, key) => (h && openSchedules[h.id] ? (
        <tr key={`${key}-dates`} className="bsch-row">
            <td />
            <td colSpan={4}>
                {canBill ? (
                    <BillingScheduleEditor
                        hotelId={h.id}
                        defaultNew={`VENDOR:${id}`}
                        onClose={() => toggleSchedule(h.id)}
                        onSaved={(res) => scheduleSaved(h, res)}
                    />
                ) : <BillingScheduleView periods={periodsOfHotel(h)} labelOf={payerLabel} />}
            </td>
        </tr>
    ) : null);
    const hotelById = (hid) => hotels.find(h => String(h.id) === String(hid));
    // A row's billing at a glance, before it is opened: all through the vendor, all direct, or how many.
    const billingSummary = (list) => {
        if (!list.length) return null;
        const through = list.filter(billedHere).length;
        const text = through === 0 ? 'Direct to hotel' : through === list.length ? 'Through vendor' : `${through} of ${list.length} through vendor`;
        return <span className="form-hint" style={{ margin: 0 }}>{text}</span>;
    };
    const chainBillingSummary = (accountId) => billingSummary(hotelsOfChain(accountId));
    // THE CHAIN'S HOTELS, under a chain grant opened with its chevron — each with how it is billed.
    const chainHotelRows = (g, i, colsBefore) => (wholeChain(g) && isOpen(g, i)
        ? (hotelsOfChain(g.accountId).length === 0
            // Said, not left blank: an open chain with nothing under it looked like the chevron did nothing.
            ? [<tr key={`${i}-none`} style={{ background: '#f9fafb' }}>
                <td />
                <td colSpan={colsBefore + 3} className="form-hint" style={{ paddingLeft: '1.25rem', margin: 0 }}>No hotels to show for this chain.</td>
              </tr>]
            : hotelsOfChain(g.accountId).map(h => [
            <tr key={`${i}-h${h.id}`} style={{ background: '#f9fafb' }}>
                <td />
                <td style={{ paddingLeft: '1.25rem' }}>{h.name}</td>
                {colsBefore > 0 && <td />}
                <td>{billingCell(h)}</td>
                <td />
            </tr>,
            scheduleRow(h, `${i}-h${h.id}`),
        ]))
        : null);
    // A row with nothing to fold keeps the chevron's width, so every chain name starts in the same place.
    const chevron = (g, i) => (foldable(g) ? (
        <button type="button" className="hc-chevron-btn" onClick={() => toggleRow(g, i)}
                aria-expanded={isOpen(g, i)}
                aria-label={isOpen(g, i) ? 'Hide hotels' : 'Show hotels'}
                title={`${wholeChain(g) ? hotelsOfChain(g.accountId).length : g.hotels.length} hotel(s)`}>
            <ChevronRight size={14} strokeWidth={2.5} />
        </button>)
        : <span aria-hidden="true" className="hc-chevron-spacer" />);

    // Ticking hotels on a WHOLE-CHAIN row narrows it — the chain's other hotels stop being reachable. Asked first, once:
    // after that the row is a list of hotels and further ticks just add or remove one. Emptying the list leaves the
    // row granting nothing (it is not saved) rather than quietly widening it back to the whole chain.
    // A hotel already on the row keeps its level; a newly ticked one starts at None, like any new grant.
    const pickHotels = (i, hotelIds) => {
        const g = grants[i];
        if (hotelIds.length && g && wholeChain(g) && hotelsOfChain(g.accountId).length > 1) {
            setNarrowing({ index: i, hotelIds });
            return;
        }
        const had = g.hotels || [];
        const hotelsNext = hotelIds.map(id => had.find(h => String(h.hotelId) === String(id)) || { hotelId: id, permissionType: 'N' });
        setGrant(i, { hotels: hotelsNext, accountId: null, chainId: chainOfRow(g) });
    };

    // None is the default: a chain or hotel added to a vendor is one it works with, not one its people may run.
    const addGrant = () => setGrants(g => [...g, { accountId: null, chainId: null, hotels: [], permissionType: 'N' }]);
    const setGrant = (i, patch) => setGrants(g => g.map((row, n) => (n === i ? { ...row, ...patch } : row)));
    const removeGrant = (i) => setGrants(g => g.filter((_, n) => n !== i));

    // INVITED THROUGH THE PARTNER'S OWN ENDPOINT, with the vendor named in the body. One path creates a
    // Cognito identity and a users row on this platform; a vendor is not a reason to have a second. What
    // the vendor adds is the link (users.uapi_credential_id) and the chains and hotels the new person
    // inherits from the Chains / Hotels tab — both done by the server.
    const addUser = async () => {
        setSavingUser(true);
        setUserError(null);
        try {
            await partnerService.invitePartnerUser(vendor.partner_id, {
                email: userForm.email.trim(),
                firstname: userForm.firstname.trim(),
                lastname: userForm.lastname.trim(),
                role: userForm.role,
                uapiCredentialId: id,
            });
            setAddingUser(false);
            setUserForm({ email: '', firstname: '', lastname: '', role: 'PARTNER_USER' });
            await load();
        } catch (e) {
            setUserError(e.response?.data?.message || e.message || 'Could not add this user');
        } finally {
            setSavingUser(false);
        }
    };

    // A PERSON'S ROLE AT THE VENDOR, and taking them off it — the partner admin's view, and the vendor's own Admins in
    // Vendor Settings (HIL-132 Q2; the server enforces both). Not the vendor's machine profile ('V'): that is what the
    // credential logs in as. And not yourself: an Admin demoting or removing themselves could leave nobody to manage it.
    const canManageUsers = canInvite;
    const isMe = (u) => !!(selfView && me && me.email && u.loginname
        && String(u.loginname).toLowerCase() === String(me.email).toLowerCase());
    const [userBusy, setUserBusy] = useState(null);          // user id while a change is in flight
    const [removingUser, setRemovingUser] = useState(null);  // the person waiting on the confirmation
    const [userMsg, setUserMsg] = useState(null);            // { ok, text } — the Users tab's own result line
    const changeUserRole = async (u, role) => {
        setUserBusy(u.id);
        setUserMsg(null);
        try {
            await uapiVendorsService.setUserRole(id, u.id, role);
            setUsers(list => list.map(x => (x.id === u.id ? { ...x, vendor_role: role } : x)));
            setUserMsg({ ok: true, text: `${[u.firstname, u.lastname].filter(Boolean).join(' ') || u.loginname} is now ${role === 'A' ? 'an Admin' : 'a User'} at ${vendor.name}` });
        } catch (e) {
            setUserMsg({ ok: false, text: e.response?.data?.error?.message || e.message || 'Could not change the role' });
        } finally {
            setUserBusy(null);
        }
    };
    const removeUser = async () => {
        const u = removingUser;
        setRemovingUser(null);
        setUserBusy(u.id);
        setUserMsg(null);
        try {
            const res = await uapiVendorsService.removeUser(id, u.id);
            setUserMsg({ ok: true, text: res?.message || 'Removed' });
            await load();
        } catch (e) {
            setUserMsg({ ok: false, text: e.response?.data?.error?.message || e.message || 'Could not remove this user' });
        } finally {
            setUserBusy(null);
        }
    };

    // SAME INVITATION, SAME RESEND as a chain or hotel user. reinviteUser is what PartnerUsers calls;
    // a vendor's person is an ordinary invited user, so the thing that chases them is the ordinary one.
    const resendInvite = async (userId) => {
        setUserError(null);
        try {
            await userService.reinviteUser(userId);
            setAccessMsg(null);
            setUserResent(userId);
        } catch (e) {
            setUserError(e.response?.data?.error?.message || e.message || 'Could not resend the invitation');
        }
    };

    const saveAccess = async () => {
        setSavingAccess(true);
        setAccessMsg(null);
        try {
            // A HALF-FILLED ROW IS NOT A GRANT. Somebody who pressed Add and then thought better of it
            // should not be told the save failed — the empty row is simply not part of the set.
            const payload = expandGrants(grants);
            const res = await uapiVendorsService.saveAccess(id, payload, loadedGrants);
            setGrants(groupGrants(payload, hotels));
            setLoadedGrants(payload);
            setAccessMsg({ ok: res?.message || 'Saved' });
        } catch (e) {
            // 409: somebody else saved in between. Nothing was written; reloading shows theirs.
            if (e.response?.status === 409) setAccessLoaded(false);
            setAccessMsg({ error: e.response?.data?.error?.message || e.response?.data?.message || e.message || 'Could not save access' });
        } finally {
            setSavingAccess(false);
        }
    };

    // DOES THIS ACTUALLY WORK? Issuing credentials proves we wrote four settings; it proves nothing
    // about whether they authenticate. A vendor told "here are your credentials" and left to discover
    // otherwise is the failure this button exists to prevent — the same question UniversalApiPage's
    // Authenticate asks, through the same endpoint.
    //
    // THE SECRET NEVER COMES BACK TO THE BROWSER. testSavedCredential names the credential by id; the
    // lambda reads and decrypts the stored settings and makes the token call itself. That is why this is
    // a test of the SAVED credential rather than of values held on this page — the page does not have
    // them, and should not.
    // ONE LOGIN, TWO USES. The button on this tab asks "do these credentials work"; the Test tab needs
    // the token that answer was produced with, to make the call as the vendor. That is the same
    // exchange, so it is the same function — and it RETURNS the token, because state arrives on the
    // next render and the console wants to authenticate and send in one go.
    //
    // CACHED UNTIL IT LAPSES. A token is valid for its whole lifetime; re-minting on every press wastes
    // the round trip and means the token in your clipboard is never the one the page holds. Thirty
    // seconds are taken off the expiry so one about to lapse is replaced rather than handed over.
    // useCallback, and it matters: the console re-runs its open-time authentication whenever this
    // function's identity changes, so a fresh closure on every render would mint a token per render
    // until one landed. The deps are the two things it actually reads.
    const testAuth = useCallback(async () => {
        if (token && token.expiresAt > Date.now()) {
            setAuthResult({ success: true, message: 'Using the cached token (still valid)' });
            return token;
        }
        setAuthRunning(true);
        setAuthResult(null);
        try {
            const res = await entityMktSettingsService.testSavedCredential(id);
            const d = res.data || res;
            let minted = null;
            const at = d.data?.accessToken;
            if (d.success === true && at) {
                const ttl = Number(d.data.expiresIn) || 3600;
                minted = {
                    credentialId: id,
                    token: at,
                    tokenType: d.data.tokenType || 'Bearer',
                    expiresAt: Date.now() + Math.max(0, ttl - 30) * 1000,
                };
            }
            setToken(minted);
            setAuthResult({
                success: d.success === true,
                message: d.message || (d.success ? 'Authenticated' : 'Authentication failed'),
            });
            return minted;
        } catch (e) {
            setToken(null);
            setAuthResult({
                success: false,
                message: e.response?.data?.message || e.message || 'Authentication failed',
            });
            return null;
        } finally {
            setAuthRunning(false);
        }
    }, [id, token]);

    const handleIssue = async () => {
        setIssuing(true);
        setActionError(null);
        // The previous result described the credentials that are about to be replaced.
        setAuthResult(null);
        try {
            // TWO SOURCES FOR THE SAME FACT, because they arrive from different endpoints and either can
            // be the one that is available. The vendor row carries marketplace_id only since it was added
            // to the list query; the credential row from listCredentials has carried it all along. Taking
            // whichever is present means this does not depend on which lambda is deployed.
            const marketplaceId = vendor.marketplace_id || credential?.marketplace_id || null;
            const res = await uapiVendorsService.issueCredentials({
                credentialId: id,
                name: vendor.name,
                partnerId: vendor.partner_id,
                marketplaceId,
            });
            setConfirmIssue(false);
            setIssued(res.issued);
            await load();
        } catch (e) {
            setActionError(e.response?.data?.message || e.message || 'Could not issue credentials');
        } finally {
            setIssuing(false);
        }
    };

    if (loading) return <div className="otx-page"><div className="loading-section"><Spinner /></div></div>;

    if (!vendor) {
        return (
            <div className="otx-page">
                <div className="empty-state">
                    <AlertCircle className="empty-icon" />
                    <p>{error || 'This vendor does not exist, or is not one you can see.'}</p>
                    <Button variant="secondary" onClick={() => navigate(backTo)}>Back</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="otx-page">
            <div className="otx-header">
                {!selfView && (
                    <div>
                        <Button variant="secondary" onClick={() => navigate(backTo)}>
                            <ArrowLeft size={16} /> Back
                        </Button>
                    </div>
                )}
                <div>
                    {renaming ? (
                        <form
                            className="vendor-rename"
                            onSubmit={async (e) => {
                                e.preventDefault();
                                const next = nameDraft.trim();
                                if (!next || next === vendor.name) { setRenaming(false); return; }
                                setSavingName(true);
                                setNameError(null);
                                try {
                                    const res = await uapiVendorsService.rename(id, next);
                                    setVendor(v => ({ ...v, name: res?.name || next }));
                                    setRenaming(false);
                                } catch (err) {
                                    setNameError(err.response?.data?.message || err.message || 'Could not rename this vendor');
                                } finally {
                                    setSavingName(false);
                                }
                            }}
                        >
                            <input
                                className="form-input vendor-rename-input"
                                value={nameDraft}
                                maxLength={255}
                                autoFocus
                                disabled={savingName}
                                aria-label="Vendor name"
                                onChange={(e) => setNameDraft(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Escape') { setRenaming(false); setNameError(null); } }}
                            />
                            <Button type="submit" disabled={savingName || !nameDraft.trim()}>{savingName ? 'Saving…' : 'Save'}</Button>
                            <Button type="button" variant="secondary" disabled={savingName}
                                onClick={() => { setRenaming(false); setNameError(null); }}>Cancel</Button>
                        </form>
                    ) : (
                        <h1 className="otx-title vendor-title">
                            {vendor.name}
                            {!readOnly && (
                                <button type="button" className="vendor-rename-btn" title="Rename this vendor" aria-label="Rename this vendor"
                                    onClick={() => { setNameDraft(vendor.name || ''); setNameError(null); setRenaming(true); }}>
                                    <Pencil size={16} />
                                </button>
                            )}
                        </h1>
                    )}
                    {nameError && <div className="os-msg os-msg-error" style={{ marginTop: '0.5rem' }}>{nameError}</div>}
                    <p className="form-hint" style={{ margin: 0 }}>
                        {/* Same two sources as the marketplace id below: whichever endpoint answered
                            first with a name for the system. */}
                        {vendor.system_name || credential?.marketplace_name || 'Unknown system'}
                        {vendor.partner_name ? ` · ${vendor.partner_name}` : ''}
                    </p>
                </div>
            </div>

            {error && <div className="os-msg os-msg-error">{error}</div>}

            <Tabs
                tabs={[
                    { key: 'credentials', label: 'Credentials', icon: <KeyRound size={20} /> },
                    { key: 'modules', label: 'Modules', count: granted.size || undefined, icon: <Puzzle size={20} /> },
                    // NEXT TO MODULES, because it is the answer to the question Modules raises: you have
                    // just decided what this vendor may call, and the obvious next thing is to make one
                    // of those calls as them.
                    { key: 'test', label: 'Test', count: testOps.length || undefined, icon: <Send size={20} /> },
                    { key: 'access', label: 'Chains / Hotels', count: grantCount || undefined, icon: <Building2 size={20} /> },
                    // The OHIP calls made for the chains and hotels on the tab before it.
                    { key: 'transactions', label: 'OHIP Transactions', icon: <Activity size={20} /> },
                    { key: 'users', label: 'Users', count: users.length, icon: <UsersIcon size={20} /> },
                    // Who the vendor is invoiced as when a hotel is billed to it. Superadmin-only to edit.
                    { key: 'billing', label: 'Billing', icon: <CreditCard size={20} /> },
                ].filter(t => TABS.includes(t.key))}
                active={tab}
                onChange={changeTab}
            />

            {tab === 'credentials' && (
                <div style={{ marginTop: '1rem' }}>
                    {/* ── UNIVERSAL API ─────────────────────────────────────────────────────────────
                        Its own section because it is its own thing: the credentials the vendor uses to
                        reach US, minted by us, replaceable by us. A vendor may later hold credentials
                        FOR its own system as well, and those are somebody else's secrets — they must not
                        sit under a button offering to regenerate them. */}
                    <Card>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                            <div>
                                <h2 className="acp-title" style={{ fontSize: '1.05rem', margin: 0 }}>Universal API</h2>
                                <p className="form-hint" style={{ margin: '0.25rem 0 0' }}>
                                    What {vendor.name} authenticates with when it calls the Universal API.
                                    {hasUapi
                                        ? ' The client secret is not shown — it is visible once, when it is generated.'
                                        : ' None issued yet, so it cannot authenticate.'}
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                                {/* Nothing to test until something has been issued. */}
                                {hasUapi && (
                                    <Button
                                        variant="secondary"
                                        onClick={testAuth}
                                        disabled={authRunning || issuing}
                                        style={{ whiteSpace: 'nowrap' }}
                                    >
                                        {authRunning ? 'Testing…' : 'Test authentication'}
                                    </Button>
                                )}
                                <Button
                                    variant={hasUapi ? 'secondary' : 'primary'}
                                    onClick={() => setConfirmIssue(true)}
                                    disabled={issuing || authRunning}
                                    style={{ whiteSpace: 'nowrap' }}
                                >
                                    {issuing
                                        ? (hasUapi ? 'Regenerating…' : 'Generating…')
                                        : (hasUapi ? 'Regenerate credentials' : 'Generate credentials')}
                                </Button>
                            </div>
                        </div>

                        {/* SHOWN WHETHER OR NOT CREDENTIALS EXIST. The endpoint is a fact about the
                            environment rather than about this vendor, and somebody setting one up wants
                            to send it alongside the client id — not discover it afterwards. */}
                        {gatewayUrl && (
                            <div style={{ marginTop: '0.75rem' }}>
                                <label className="form-label">Universal API URL</label>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <code style={{ flex: 1, wordBreak: 'break-all' }}>{gatewayUrl}</code>
                                    <CopyButton value={gatewayUrl} />
                                </div>
                            </div>
                        )}

                        {hasUapi ? (
                            <SettingsTable
                                title="Credentials"
                                rows={uapiSettings}
                                nameOf={nameOf}
                                showAll={showAllSecrets}
                                onToggleShowAll={setShowAllSecrets}
                            />
                        ) : (
                            <p className="form-hint" style={{ marginTop: '0.75rem', color: '#9ca3af' }}>
                                Generating issues a client id and secret. The secret is shown once and cannot be
                                retrieved afterwards, so do it when you are ready to send it.
                            </p>
                        )}
                    </Card>

                    {/* Anything else stored on this credential. Collapsed like the rest, but the card only
                        exists when there is something in it — a value somebody entered by hand is not ours
                        to quietly drop out of the screen. */}
                    {otherSettings.length > 0 && (
                        <Card className="vendor-section-gap">
                            <SettingsTable
                                title="Other settings"
                                hint="Defined by the system's marketplace record and entered by hand. Regenerating the Universal API credentials above leaves these untouched."
                                rows={otherSettings}
                                nameOf={nameOf}
                                showAll={showAllSecrets}
                                onToggleShowAll={setShowAllSecrets}
                            />
                        </Card>
                    )}

                    {/* THE RESULT SAYS WHICH CREDENTIALS IT TESTED. On a page with a Regenerate button
                        beside it, "Authenticated" on its own is ambiguous the moment somebody has
                        pressed both — so it names the vendor and clears itself when new ones are
                        issued (handleIssue resets it). */}
                    {authResult && (
                        <div
                            className={`os-msg ${authResult.success ? 'os-msg-success' : 'os-msg-error'}`}
                            style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                            {authResult.success ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                            <span>
                                {authResult.success
                                    ? `${vendor.name}'s credentials authenticated against the Universal API.`
                                    : `${vendor.name}'s credentials did not authenticate — ${authResult.message}`}
                            </span>
                        </div>
                    )}

                    {actionError && <div className="os-msg os-msg-error" style={{ marginTop: '0.75rem' }}>{actionError}</div>}
                </div>
            )}

            {tab === 'modules' && (
                <div style={{ marginTop: '1rem' }}>
                    <Card>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                            <div>
                                <h2 className="acp-title" style={{ fontSize: '1.05rem', margin: 0 }}>Universal API modules</h2>
                                <p className="form-hint" style={{ margin: '0.25rem 0 0' }}>
                                    What {vendor.name} may call. A module is a top-level resource —
                                    <code> /crm</code> profiles, <code>/rsv</code> reservations — and this is
                                    separate from WHERE it may call: the properties are on Chains / Hotels.
                                </p>
                            </div>
                            {!readOnly && (
                                <Button onClick={saveModules} disabled={savingModules || !modulesLoaded}
                                    style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                                    {savingModules ? 'Saving…' : 'Save'}
                                </Button>
                            )}
                        </div>

                        {!modulesLoaded ? (
                            <div className="loading-section"><Spinner /></div>
                        ) : routes.length === 0 ? (
                            <p className="form-hint" style={{ marginTop: '0.75rem', color: '#9ca3af' }}>
                                No modules are defined on this platform's Universal API.
                            </p>
                        ) : (
                            <table className="acp-table" style={{ marginTop: '0.75rem' }}>
                                <thead><tr><th style={{ width: '3rem' }} aria-label="Granted" /><th>Module</th><th /></tr></thead>
                                <tbody>
                                    {routes.map(r => {
                                        const locked = isLocked(r);
                                        return (
                                            <tr key={r.id}>
                                                <td>
                                                    {/* THE LOCKED ONE IS SHOWN TICKED AND DISABLED, not hidden.
                                                        /oauth is the token endpoint: without it the vendor
                                                        cannot log in at all, so revoking it presents as a
                                                        broken credential rather than a missing permission.
                                                        Hiding it would leave somebody wondering where it went. */}
                                                    <input
                                                        type="checkbox"
                                                        checked={locked || granted.has(Number(r.id))}
                                                        disabled={readOnly || locked || savingModules}
                                                        onChange={() => toggleModule(Number(r.id))}
                                                        aria-label={`Grant ${r.path}`}
                                                    />
                                                </td>
                                                <td style={{ fontWeight: 600, fontFamily: 'monospace' }}>{r.path}</td>
                                                <td style={{ color: '#6b7280' }}>
                                                    {locked ? 'Always granted — the token endpoint' : ''}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}

                        {modulesMsg?.error && <div className="os-msg os-msg-error" style={{ marginTop: '0.75rem' }}>{modulesMsg.error}</div>}
                        {modulesMsg?.ok && <div className="os-msg os-msg-success" style={{ marginTop: '0.75rem' }}>{modulesMsg.ok}</div>}
                    </Card>
                </div>
            )}

            {/* ── TEST ─────────────────────────────────────────────────────────────────────────────
                Make a real call as this vendor, from this screen.

                WHAT IT OFFERS IS WHAT THE VENDOR MAY CALL — the operations of its granted modules, and
                nothing else. Showing the whole Universal API here and letting the gateway refuse most of
                it would turn "what can Thynk do" into a page of 403s to read through; showing only the
                grants makes the Modules tab's decision visible as a list of calls.

                IT IS A REAL REQUEST, with the vendor's own token and one of its own x-hotelid bindings.
                Not a simulation and not a proxy: the console sends it from the browser exactly as the
                vendor's server would, so what comes back — including the redactions applied on the way
                out — is what the vendor gets. */}
            {tab === 'test' && (
                <div style={{ marginTop: '1rem' }}>
                    <Card>
                        <h2 className="acp-title" style={{ fontSize: '1.05rem', margin: 0 }}>
                            Call the Universal API as {vendor.name}
                        </h2>
                        <p className="form-hint" style={{ margin: '0.25rem 0 0' }}>
                            The calls its granted modules cover. Test signs in with this vendor&rsquo;s own
                            credentials and sends a real request to <code>{gatewayUrl || 'the gateway'}</code>
                            {' '}— a write here changes the property&rsquo;s data.
                        </p>

                        {testError && <div className="os-msg os-msg-error" style={{ marginTop: '0.75rem' }}>{testError}</div>}

                        {!testLoaded && !testError && <div className="loading-section"><Spinner /></div>}

                        {/* THE THREE THINGS A CALL NEEDS, EACH REPORTED SEPARATELY. Credentials,
                            modules and a property binding are three different gaps with three different
                            fixes, and "you cannot test this vendor" would name none of them. */}
                        {testLoaded && !hasUapi && (
                            <p className="form-hint" style={{ marginTop: '0.75rem', color: '#9ca3af' }}>
                                This vendor has no Universal API credentials yet, so there is nobody to call
                                as. Issue them on the Credentials tab.
                            </p>
                        )}
                        {testLoaded && hasUapi && testOps.length === 0 && (
                            <p className="form-hint" style={{ marginTop: '0.75rem', color: '#9ca3af' }}>
                                No modules are granted to this vendor, so there is nothing it may call.
                                Grant one on the Modules tab.
                            </p>
                        )}
                        {/* SENT TO THE TAB THAT FIXES IT. This used to name Legacy › Universal API,
                            which is superadmin's — a partner admin reading it was told to open a screen
                            they cannot reach. Routing is now issued on this vendor's own Chains /
                            Hotels tab, so that is where the button goes. */}
                        {testLoaded && hasUapi && testOps.length > 0 && testBindings.length === 0 && (
                            <div className="os-msg os-msg-error" style={{ marginTop: '0.75rem' }}>
                                <AlertCircle size={16} />
                                <span>
                                    No property is routed to these credentials, so there is no
                                    {' '}<code>x-hotelid</code> to send and every call would be refused.
                                    {' '}<button
                                        type="button"
                                        onClick={() => changeTab('access')}
                                        style={{ background: 'none', border: 'none', padding: 0,
                                                 color: '#4a6cf7', font: 'inherit', fontWeight: 600,
                                                 cursor: 'pointer', textDecoration: 'underline' }}
                                    >
                                        Add one on Chains / Hotels
                                    </button>.
                                </span>
                            </div>
                        )}

                        {/* WHICH PROPERTY, CHOSEN ONCE — and who we are signed in as. Both are settled
                            here rather than inside each call: the answer is the same for every call in
                            a sitting, and a question asked once per Test button is a question asked
                            twenty times. */}
                        {testLoaded && testBindings.length > 0 && (
                            <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap',
                                          alignItems: 'flex-end', gap: '1rem' }}>
                                <div style={{ flex: '1 1 22rem', minWidth: '18rem' }}>
                                    <label className="form-label" htmlFor="vt-xhotelid">
                                        Property to call (<code>x-hotelid</code>)
                                    </label>
                                    <select
                                        id="vt-xhotelid"
                                        className="form-input"
                                        value={testXHotelId}
                                        onChange={(e) => setTestXHotelId(e.target.value)}
                                    >
                                        <option value="">Choose a property…</option>
                                        {testBindings.map(b => (
                                            <option key={b.id} value={b.uapi_hotel_id}>
                                                {b.hotelname || `Hotel ${b.hotel_id}`} — {b.system_name}
                                                {b.label ? ` (${b.label})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {selectedBinding && (
                                        <p className="form-hint" style={{ margin: '0.35rem 0 0' }}>
                                            <code style={{ fontSize: '0.78rem', wordBreak: 'break-all' }}>
                                                {selectedBinding.uapi_hotel_id}
                                            </code>
                                            <CopyButton value={selectedBinding.uapi_hotel_id} />
                                        </p>
                                    )}
                                </div>

                                {/* SIGNED IN ALREADY, OR SAYING WHY NOT. The token is minted on arrival;
                                    this reports the outcome rather than offering a button for a step
                                    nobody has to think about. */}
                                <div style={{ flex: '0 1 auto', paddingBottom: '0.15rem' }}>
                                    {authRunning ? (
                                        <span className="form-hint" style={{ margin: 0 }}>
                                            <Spinner size="sm" /> Signing in as {vendor.name}…
                                        </span>
                                    ) : token && token.expiresAt > Date.now() ? (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                                                       color: '#047857', fontSize: '0.85rem', fontWeight: 600 }}>
                                            <CheckCircle2 size={15} /> Signed in as {vendor.name}
                                        </span>
                                    ) : (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                                                       color: '#ef4444', fontSize: '0.85rem' }}>
                                            <XCircle size={15} />
                                            {authResult?.message || 'Not signed in'}
                                            <Button variant="secondary" onClick={testAuth} disabled={authRunning}
                                                style={{ marginLeft: '0.35rem' }}>
                                                Retry
                                            </Button>
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </Card>

                    {/* ONE CARD PER MODULE, in the order the server returned them. A flat list of a
                        hundred operations hides the thing the reader came to check, which is which
                        modules are on. */}
                    {testLoaded && testModules.map(m => {
                        const ops = testOps.filter(o => o.module_path === m.path);
                        if (!ops.length) return null;
                        return (
                            <Card key={m.uapi_route_id} className="vendor-section-gap">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <code className="lu-mono" style={{ fontWeight: 600 }}>{m.path}</code>
                                    <span className="form-hint" style={{ margin: 0 }}>
                                        {ops.length} call{ops.length === 1 ? '' : 's'}
                                        {/* A SELECTED grant is an allow list, so the count below is the
                                            whole of what was granted — not a page of a longer list. */}
                                        {m.operation_mode === 'SELECTED' ? ' · only these were granted' : ''}
                                    </span>
                                </div>
                                <table className="acp-table" style={{ marginTop: '0.5rem' }}>
                                    <tbody>
                                        {ops.map(o => (
                                            <tr key={o.id}>
                                                <td style={{ width: '5rem' }}>
                                                    <span className={`lu-method lu-method-${String(o.method).toLowerCase()}`}>
                                                        {o.method}
                                                    </span>
                                                </td>
                                                <td>
                                                    <code className="lu-mono">{o.full_path}</code>
                                                    {o.description && (
                                                        <div className="form-hint" style={{ margin: 0 }}>{o.description}</div>
                                                    )}
                                                </td>
                                                <td style={{ width: '6rem', textAlign: 'right' }}>
                                                    <Button
                                                        variant="secondary"
                                                        onClick={() => setTestOperation(o)}
                                                        disabled={!hasUapi || !testXHotelId}
                                                        title={testBindings.length === 0
                                                            ? 'No property is routed to these credentials, so there is nowhere to send this'
                                                            : !testXHotelId
                                                                ? 'Choose a property above first'
                                                                : `Send a real ${o.method} as ${vendor.name} to `
                                                                  + `${selectedBinding?.hotelname || 'the chosen property'}`}
                                                    >
                                                        <Send size={14} /> Test
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Card>
                        );
                    })}
                </div>
            )}

            {tab === 'access' && (
                <div style={{ marginTop: '1rem' }}>
                    <Card>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                            <div>
                                <h2 className="acp-title" style={{ fontSize: '1.05rem', margin: 0 }}>Chains and hotels</h2>
                                <p className="form-hint" style={{ margin: '0.25rem 0 0' }}>
                                    The hotels {vendor.name}'s users can see, and who each one is billed to, by date: directly
                                    to the hotel, or through {vendor.name} — e.g. direct until 31 March, through the vendor from
                                    1 April. Open a chain to see its hotels.
                                </p>
                            </div>
                            {!readOnly && (
                                <Button variant="secondary" onClick={addGrant} style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                                    <Plus size={16} /> Add
                                </Button>
                            )}
                        </div>

                        {/* SET ONCE, INHERITED BY EVERY USER. The grants belong to the vendor, not to a
                            particular person — so they can be decided before anybody has been added, and
                            a user added next month starts with them rather than being granted by hand. */}
                        {grants.length === 0 ? (
                            <p className="form-hint" style={{ marginTop: '0.75rem', color: '#9ca3af' }}>
                                Nothing granted. {vendor.name}'s users can sign in but will see no chains or hotels.
                            </p>
                        ) : (
                            <table className="acp-table" style={{ marginTop: '0.75rem' }}>
                                <thead>
                                    <tr><th>Chain</th><th>Hotel</th><th>Level</th><th>Billing</th><th aria-label="Actions" /></tr>
                                </thead>
                                <tbody>
                                    {readOnly && grants.map((g, i) => (
                                        <React.Fragment key={i}>
                                            <tr>
                                                <td>{chevron(g, i)}{chainNameOf(g) || <EmptyValue />}</td>
                                                <td>{rowHotelNames(g).join(', ') || (g.accountId ? 'All hotels' : <EmptyValue />)}</td>
                                                <td>{rowLevel(g, i, false)}</td>
                                                <td>{rowBilling(g)}</td>
                                                <td />
                                            </tr>
                                            {idsOf(g).length === 1 && scheduleRow(hotelById(idsOf(g)[0]), `${i}-one`)}
                                            {hotelLevelRows(g, i, false)}
                                            {chainHotelRows(g, i, 1)}
                                        </React.Fragment>
                                    ))}
                                    {!readOnly && grants.map((g, i) => (
                                        <React.Fragment key={i}>
                                        <tr>
                                            {/* ONE OR THE OTHER, and choosing one clears the other. A row naming
                                                both is ambiguous, and the server refuses it — better to make it
                                                impossible to express than to explain it afterwards. */}
                                            <td style={{ minWidth: '14rem', display: 'flex', alignItems: 'center' }}>
                                                {chevron(g, i)}
                                                <SearchSelect
                                                    items={chainItems}
                                                    value={chainOfRow(g)}
                                                    onChange={(next) => setGrant(i, { accountId: next, chainId: next, hotels: [] })}
                                                    placeholder="Whole chain…"
                                                    searchPlaceholder="Search chains…"
                                                    emptyLabel="This partner has no chains."
                                                    allowClear
                                                    clearLabel="— none —"
                                                />
                                            </td>
                                            <td style={{ minWidth: '14rem' }}>
                                                <SearchSelect
                                                    multiple
                                                    items={chainOfRow(g) ? hotelItems.filter(h => String(h.accountId) === String(chainOfRow(g))) : hotelItems}
                                                    value={idsOf(g)}
                                                    onChange={(next) => pickHotels(i, next)}
                                                    countLabel={(n) => `${n} hotels`}
                                                    placeholder={g.accountId ? 'All hotels' : 'Choose hotels…'}
                                                    searchPlaceholder="Search hotels…"
                                                    emptyLabel="This partner has no hotels."
                                                    allowClear
                                                    clearLabel="— none —"
                                                />
                                            </td>
                                            <td>{rowLevel(g, i, true)}</td>
                                            <td>{rowBilling(g)}</td>
                                            <td style={{ textAlign: 'right' }}>
                                                <button
                                                    type="button"
                                                    title="Remove this grant"
                                                    aria-label="Remove this grant"
                                                    onClick={() => removeGrant(i)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer',
                                                             color: '#ef4444', padding: '0.25rem' }}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                        {idsOf(g).length === 1 && scheduleRow(hotelById(idsOf(g)[0]), `${i}-one`)}
                                        {hotelLevelRows(g, i, true)}
                                        {chainHotelRows(g, i, 1)}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {!readOnly && <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <Button onClick={saveAccess} disabled={savingAccess}>
                                {savingAccess ? 'Saving…' : 'Save'}
                            </Button>
                            {/* SAVING RE-SYNCS EVERY USER, which is worth saying: the change is not to a
                                list on a screen, it is to what each of these people can open. */}
                            <span className="form-hint" style={{ margin: 0 }}>
                                Saving applies this to {users.length === 0
                                    ? 'every user added to this vendor'
                                    : `all ${users.length} of ${vendor.name}'s user${users.length === 1 ? '' : 's'}`}.
                            </span>
                        </div>}

                        {accessMsg?.error && <div className="os-msg os-msg-error" style={{ marginTop: '0.75rem' }}>{accessMsg.error}</div>}
                        {accessMsg?.ok && <div className="os-msg os-msg-success" style={{ marginTop: '0.75rem' }}>{accessMsg.ok}</div>}
                    </Card>

                    {/* ── UNIVERSAL API ROUTING ─────────────────────────────────────────────────────
                        The grants above decide what this vendor's PEOPLE can open in the portal. This
                        decides what its SOFTWARE can reach through the API, and the two are different
                        questions about the same properties — so they sit on one tab, in that order.

                        An x-hotelid is issued per (property, backend system) pairing and names the pair.
                        The vendor sends it as a header; it never learns which PMS is behind it or what
                        the property is called there, so the backend can be changed underneath without
                        telling anybody. */}
                    <Card className="vendor-section-gap">
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                            <div>
                                <h2 className="acp-title" style={{ fontSize: '1.05rem', margin: 0 }}>
                                    <Link2 size={17} style={{ verticalAlign: '-2px', marginRight: '0.35rem', color: '#6b7280' }} />
                                    Universal API routing
                                </h2>
                                <p className="form-hint" style={{ margin: '0.25rem 0 0' }}>
                                    The properties {vendor.name} can call, and the backend each one reaches.
                                    Every row is an <code>x-hotelid</code> issued to this vendor — it presents
                                    that header, and we route the call.
                                </p>
                            </div>
                            {!readOnly && <Button
                                variant="secondary"
                                onClick={openAddBinding}
                                disabled={addingBinding || !accessLoaded}
                                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                            >
                                <Plus size={16} /> Add Credential
                            </Button>}
                        </div>

                        {bindings.length === 0 ? (
                            <p className="form-hint" style={{ marginTop: '0.75rem', color: '#9ca3af' }}>
                                Nothing routed yet. {vendor.name} can authenticate but has no property to call —
                                every request would be refused for an unknown <code>x-hotelid</code>.
                            </p>
                        ) : (
                            <table className="acp-table" style={{ marginTop: '0.75rem' }}>
                                <thead>
                                    <tr>
                                        <th>Property</th><th>Backend</th><th>Credential</th>
                                        <th>x-hotelid</th><th aria-label="Actions" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {bindings.map(b => (
                                        <tr key={b.id}>
                                            <td>
                                                <div style={{ fontWeight: 600 }}>{b.hotelname || `Hotel ${b.hotel_id}`}</div>
                                                {b.account_name && <div className="form-hint" style={{ margin: 0 }}>{b.account_name}</div>}
                                            </td>
                                            <td>
                                                {b.system_name}
                                                {b.label && <div className="form-hint" style={{ margin: 0 }}>{b.label}</div>}
                                            </td>
                                            <td style={{ color: '#6b7280' }}>{b.credential_name || <EmptyValue />}</td>
                                            <td>
                                                {/* THE ONE VALUE SOMEBODY CAME HERE TO COPY. It is the
                                                    whole of what the vendor is given, so it is shown in
                                                    full with a copy button rather than truncated. */}
                                                <code style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>{b.uapi_hotel_id}</code>
                                                <CopyButton value={b.uapi_hotel_id} />
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {!readOnly && <button
                                                    type="button"
                                                    title="Retire this routing"
                                                    aria-label={`Retire the routing for ${b.hotelname || 'this property'}`}
                                                    onClick={() => setConfirmRemoveBinding(b)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer',
                                                             color: '#ef4444', padding: '0.25rem' }}
                                                >
                                                    <Trash2 size={16} />
                                                </button>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {/* A PANEL, NOT A DIALOG (STYLE.md "No Modals"). It also has to be one: the
                            credential picker below takes over the screen on its own, and a full-screen
                            takeover opened from inside an overlay is a stack nobody can reason about. */}
                        {addingBinding && (
                            <div style={{ marginTop: '1rem', padding: '1rem', border: '1px solid #e5e7eb',
                                          borderRadius: '8px', background: '#f9fafb' }}>
                                <h3 className="acp-title" style={{ fontSize: '0.95rem', margin: '0 0 0.75rem' }}>
                                    Route a property to {vendor.name}
                                </h3>

                                <label className="form-label" htmlFor="vb-hotel">Property</label>
                                <SearchSelect
                                    id="vb-hotel"
                                    items={grantedHotels.map(h => ({ id: h.id, label: h.name, code: h.account_name }))}
                                    value={bindForm.hotelId}
                                    onChange={(next) => setBindForm(f => ({ ...f, hotelId: next, credential: null }))}
                                    placeholder="Choose a property…"
                                    searchPlaceholder="Search properties…"
                                    emptyLabel="No property is granted to this vendor yet."
                                />
                                <p className="form-hint" style={{ margin: '0.25rem 0 0' }}>
                                    Only the properties granted above can be routed — and only once those grants
                                    are <strong>saved</strong>. A property is offered here as soon as you grant it,
                                    but the save below will refuse it until the grant itself is saved.
                                </p>

                                <label className="form-label" htmlFor="vb-system" style={{ marginTop: '0.75rem' }}>Backend system</label>
                                <select
                                    id="vb-system"
                                    className="form-input"
                                    value={bindForm.marketplaceId}
                                    onChange={(e) => setBindForm(f => ({ ...f, marketplaceId: e.target.value, credential: null }))}
                                >
                                    <option value="">Choose a system…</option>
                                    {systems.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>

                                {/* THE SHARED SELECTOR, not a picker of its own — the same control sync,
                                    Payment Porter and Vault use, so "Add Credential" opens the screen
                                    everybody already knows. It offers this property's credentials and
                                    its chain's and partner's, and can mint a new one in place. */}
                                {bindForm.marketplaceId && bindForm.hotelId && (
                                    <div style={{ marginTop: '0.75rem' }}>
                                        <CredentialSelector
                                            marketplaceId={Number(bindForm.marketplaceId)}
                                            hotelId={Number(bindForm.hotelId)}
                                            accountId={grantedHotels.find(h => String(h.id) === String(bindForm.hotelId))?.account_id}
                                            partnerId={vendor.partner_id}
                                            value={bindForm.credential}
                                            onChange={(c) => setBindForm(f => ({ ...f, credential: c }))}
                                            title="Backend credential"
                                            subtitle="What the Universal API authenticates to that system with. The vendor never sees it."
                                        />
                                    </div>
                                )}

                                <label className="form-label" htmlFor="vb-code" style={{ marginTop: '0.75rem' }}>
                                    Property code at that system <span className="form-hint">optional</span>
                                </label>
                                <input
                                    id="vb-code"
                                    className="form-input"
                                    value={bindForm.hotelidOverride}
                                    onChange={(e) => setBindForm(f => ({ ...f, hotelidOverride: e.target.value }))}
                                    placeholder="Leave blank to use the code on the credential"
                                />

                                <label className="form-label" htmlFor="vb-label" style={{ marginTop: '0.75rem' }}>
                                    Name for this routing <span className="form-hint">optional</span>
                                </label>
                                <input
                                    id="vb-label"
                                    className="form-input"
                                    maxLength={255}
                                    value={bindForm.label}
                                    onChange={(e) => setBindForm(f => ({ ...f, label: e.target.value }))}
                                    placeholder="Only needed when one property is routed more than once"
                                />

                                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                                    <Button onClick={saveBinding} disabled={savingBinding}>
                                        {savingBinding ? 'Issuing…' : 'Issue x-hotelid'}
                                    </Button>
                                    <Button variant="secondary" onClick={() => setAddingBinding(false)} disabled={savingBinding}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}

                        {bindingMsg?.error && <div className="os-msg os-msg-error" style={{ marginTop: '0.75rem' }}>{bindingMsg.error}</div>}
                        {bindingMsg?.ok && <div className="os-msg os-msg-success" style={{ marginTop: '0.75rem' }}>{bindingMsg.ok}</div>}
                    </Card>
                </div>
            )}

            {tab === 'transactions' && (
                <OhipTransactionsPage vendorId={Number(id)} embedded />
            )}

            {tab === 'billing' && (
                <div style={{ marginTop: '1rem' }}>
                    <BillingTab entity="vendor" entityId={id} />
                </div>
            )}

            {tab === 'users' && (
                <div style={{ marginTop: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                        <p className="form-hint" style={{ margin: 0 }}>
                            The people at {vendor.name} who can sign in. They appear in the partner's user list
                            and in Admin &gt; Users like anybody else, linked to this vendor, and inherit whatever
                            the Chains / Hotels tab grants.
                            {/* HIL-132 Q1: Role and Level are two different things, and both say Admin/User. */}
                            <br />
                            <strong>Role</strong> decides who can manage {vendor.name}'s settings and invite people. What
                            each person can do at a chain or hotel is set by the <strong>Level</strong> on the Chains / Hotels tab, not by their Role.
                        </p>
                        {canInvite && (
                            <Button onClick={() => { setUserError(null); setAddingUser(true); }} style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                                <Plus size={16} /> Add user
                            </Button>
                        )}
                    </div>
                    {userMsg && (
                        <div className={`os-msg ${userMsg.ok ? 'os-msg-success' : 'os-msg-error'}`} style={{ marginBottom: '0.75rem' }}
                            onClick={() => setUserMsg(null)}>
                            {userMsg.text}
                        </div>
                    )}
                    {users.length === 0 ? (
                        <div className="empty-state">
                            <AlertCircle className="empty-icon" />
                            <p>No users yet for this vendor.</p>
                        </div>
                    ) : (
                        <table className="acp-table">
                            <thead>
                                <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th aria-label="Actions" /></tr>
                            </thead>
                            <tbody>
                                {users.map(u => (
                                    <tr key={u.id}>
                                        <td style={{ fontWeight: 600 }}>
                                            {[u.firstname, u.lastname].filter(Boolean).join(' ') || '—'}
                                        </td>
                                        <td style={{ color: '#6b7280' }}>{u.loginname || <EmptyValue />}</td>
                                        {/* The role AT THE VENDOR, spelled out (users.uapi_vendor_role, V609). People hold no
                                            partner row since V608; 'V' is the vendor's own machine profile, named as such. */}
                                        <td>
                                            {/* CHANGEABLE for a person, in the partner admin's view: Admin may invite the
                                                vendor's people from Vendor Settings, User may not. */}
                                            {canManageUsers && !isMe(u) && ['A', 'U'].includes(u.vendor_role) ? (
                                                <select
                                                    className="form-input"
                                                    style={{ width: 'auto', padding: '0.2rem 0.5rem' }}
                                                    value={u.vendor_role}
                                                    disabled={userBusy === u.id}
                                                    onChange={(e) => changeUserRole(u, e.target.value)}
                                                    aria-label={`Role of ${[u.firstname, u.lastname].filter(Boolean).join(' ') || u.loginname} at ${vendor.name}`}
                                                >
                                                    <option value="U">User</option>
                                                    <option value="A">Admin</option>
                                                </select>
                                            ) : (u.vendor_role || u.permission_type) === 'A' ? 'Admin'
                                                : (u.vendor_role || u.permission_type) === 'U' ? 'User'
                                                    : u.permission_type === 'V' ? 'Vendor (machine)'
                                                        : <EmptyValue />}
                                        </td>
                                        {/* INVITED IS NOT IN. Somebody who has not accepted yet cannot reach
                                            anything, and a list that shows them the same as an active user is
                                            how "they say they have no access" becomes a support call. */}
                                        <td>
                                            {u.has_pending_invitation
                                                ? <Badge variant="amber" title="Invitation sent, not accepted yet">Invited</Badge>
                                                : u.lastlogin
                                                    ? <span style={{ color: '#15803d' }}>Active</span>
                                                    : <EmptyValue>Accepted</EmptyValue>}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            {/* !! — MySQL hands back has_pending_invitation as 0/1, and a bare 0 is printed by React: every
                                                Accepted user showed a stray "0" here (HIL-131). */}
                                            {canInvite && !!u.has_pending_invitation && (
                                                <button
                                                    type="button"
                                                    title={userResent === u.id ? 'Invitation resent' : 'Resend invitation'}
                                                    aria-label="Resend invitation"
                                                    onClick={() => resendInvite(u.id)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer',
                                                             color: userResent === u.id ? '#15803d' : '#4a6cf7', padding: '0.25rem' }}
                                                >
                                                    <Mail size={16} />
                                                </button>
                                            )}
                                            {canManageUsers && !isMe(u) && ['A', 'U'].includes(u.vendor_role) && (
                                                <button
                                                    type="button"
                                                    title={`Remove from ${vendor.name}`}
                                                    aria-label={`Remove from ${vendor.name}`}
                                                    disabled={userBusy === u.id}
                                                    onClick={() => setRemovingUser(u)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '0.25rem' }}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* TAKING SOMEBODY OFF THE VENDOR is confirmed, with what it does and what it leaves. */}
                    {removingUser && (
                        <ConfirmModal
                            title={`Remove from ${vendor.name}?`}
                            message={`${[removingUser.firstname, removingUser.lastname].filter(Boolean).join(' ') || removingUser.loginname} `
                                + `will no longer be a person at ${vendor.name}, and loses the chains and hotels they reach through it. `
                                + 'Their sign-in, and anything they are granted elsewhere, are kept.'}
                            confirmLabel="Remove"
                            danger
                            onConfirm={removeUser}
                            onCancel={() => setRemovingUser(null)}
                        />
                    )}
                </div>
            )}

            {/* NARROWING A WHOLE CHAIN TO ONE HOTEL is confirmed, naming what stops being reachable. */}
            {narrowing && (() => {
                const g = grants[narrowing.index] || {};
                const chainName = chainItems.find(c => String(c.id) === String(g.accountId))?.label || 'this chain';
                const keep = new Set(narrowing.hotelIds.map(String));
                const names = hotelItems.filter(h => keep.has(String(h.id))).map(h => h.label);
                const hotelName = names.length === 1 ? names[0] : `${names.length} hotels`;
                const others = hotelsOfChain(g.accountId).filter(h => !keep.has(String(h.id))).map(h => h.name);
                return (
                    <ConfirmModal
                        title={`Narrow ${chainName} to ${hotelName}?`}
                        message={`${vendor.name}'s users can reach the whole of ${chainName} now. This grants ${names.join(', ')} only, `
                            + `so they will no longer reach ${others.join(', ')}. Tick more hotels in the list afterwards to keep `
                            + 'them too. Nothing changes until you save.'}
                        confirmLabel={`${hotelName} only`}
                        danger
                        onConfirm={() => { setGrant(narrowing.index, { hotels: narrowing.hotelIds.map(hotelId => ({ hotelId, permissionType: g.permissionType })), accountId: null, chainId: g.accountId }); setNarrowing(null); }}
                        onCancel={() => setNarrowing(null)}
                    />
                );
            })()}

            {addingUser && canInvite && (
                <ConfirmModal
                    title={`Add a user for ${vendor.name}`}
                    confirmLabel={savingUser ? 'Inviting…' : 'Invite'}
                    cancelLabel="Cancel"
                    onConfirm={() => {
                        if (savingUser) return;
                        if (!userForm.email.trim() || !userForm.firstname.trim() || !userForm.lastname.trim()) return;
                        addUser();
                    }}
                    onCancel={() => { if (!savingUser) setAddingUser(false); }}
                >
                    <div style={{ textAlign: 'left' }}>
                        <label className="form-label" htmlFor="vu-email">Email</label>
                        <input id="vu-email" className="form-input" type="email" autoFocus
                            value={userForm.email}
                            onChange={(e) => setUserForm(f => ({ ...f, email: e.target.value }))}
                            placeholder="name@thynk.cloud" />

                        <label className="form-label" htmlFor="vu-first" style={{ marginTop: '0.75rem' }}>First name</label>
                        <input id="vu-first" className="form-input"
                            value={userForm.firstname}
                            onChange={(e) => setUserForm(f => ({ ...f, firstname: e.target.value }))} />

                        <label className="form-label" htmlFor="vu-last" style={{ marginTop: '0.75rem' }}>Last name</label>
                        <input id="vu-last" className="form-input"
                            value={userForm.lastname}
                            onChange={(e) => setUserForm(f => ({ ...f, lastname: e.target.value }))} />

                        {/* THE PARTNER-LEVEL ROLE, which is a different question from the chain and hotel
                            levels on the other tab: this one says what they are within the partner, those
                            say which properties they can reach. */}
                        <label className="form-label" htmlFor="vu-role" style={{ marginTop: '0.75rem' }}>Role at {vendor.name}</label>
                        <select id="vu-role" className="form-input"
                            value={userForm.role}
                            onChange={(e) => setUserForm(f => ({ ...f, role: e.target.value }))}>
                            <option value="PARTNER_USER">User</option>
                            <option value="PARTNER_ADMIN">Admin</option>
                        </select>

                        <p className="form-hint" style={{ marginTop: '0.75rem' }}>
                            They are emailed a temporary password and will appear under
                            {` ${vendor.partner_name || 'the partner'}`}, linked to {vendor.name}.
                            {!accessLoaded
                                ? ' They inherit whatever the Chains / Hotels tab grants.'
                                : grantCount > 0
                                    ? ` They inherit the ${grantCount} chain/hotel grant${grantCount === 1 ? '' : 's'} on the Chains / Hotels tab.`
                                    : ' Nothing is granted on the Chains / Hotels tab yet, so they will see no properties.'}
                        </p>

                        {userError && <div className="os-msg os-msg-error" style={{ marginTop: '0.75rem' }}>{userError}</div>}
                    </div>
                </ConfirmModal>
            )}

            {/* RE-ISSUING IS DESTRUCTIVE AND DOES NOT LOOK IT. The old client secret stops working the
                moment the new one exists, so a vendor mid-integration goes down until somebody sends
                them the new one. Said before, not after. */}
            {confirmIssue && (
                <ConfirmModal
                    title={hasUapi
                        ? `Regenerate Universal API credentials for ${vendor.name}?`
                        : `Generate Universal API credentials for ${vendor.name}?`}
                    message={hasUapi
                        ? `This issues a new client id and secret. ${vendor.name}'s current ones stop working `
                          + 'immediately and cannot be recovered — anything still using them fails until the new '
                          + 'ones are sent. The secret is shown once, now.'
                        : `This issues a client id and secret for ${vendor.name} to call the Universal API with. `
                          + 'The secret is shown once and cannot be retrieved afterwards, so be ready to send it.'}
                    confirmLabel={issuing
                        ? (hasUapi ? 'Regenerating…' : 'Generating…')
                        : (hasUapi ? 'Regenerate' : 'Generate')}
                    danger={hasUapi}
                    onConfirm={handleIssue}
                    onCancel={() => { if (!issuing) setConfirmIssue(false); }}
                />
            )}

            {issued && (
                <ConfirmModal
                    title="Universal API credentials generated"
                    confirmLabel="Done"
                    cancelLabel="Close"
                    onConfirm={() => setIssued(null)}
                    onCancel={() => setIssued(null)}
                >
                    <div style={{ textAlign: 'left' }}>
                        <div className="os-msg os-msg-warning" style={{ marginBottom: '0.75rem' }}>
                            This is the only time the client secret is visible. Send it to {vendor.name} now —
                            it cannot be retrieved afterwards.
                        </div>
                        {/* The URL is repeated here deliberately. This dialog is what somebody copies
                            into the message they send the vendor, and a client id with no endpoint is
                            half an answer — the secret cannot be shown again to complete it later. */}
                        {[
                            ['Universal API URL', gatewayUrl],
                            ['Client ID', issued.clientId],
                            ['Client Secret', issued.clientSecret],
                            ['Token URL', issued.tokenUrl],
                            ['Scope', issued.scope],
                        ].filter(([, v]) => v).map(([label, value]) => (
                            <div key={label} style={{ marginBottom: '0.6rem' }}>
                                <label className="form-label">{label}</label>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <code style={{ flex: 1, wordBreak: 'break-all' }}>{value}</code>
                                    <CopyButton value={value} />
                                </div>
                            </div>
                        ))}
                    </div>
                </ConfirmModal>
            )}

            {/* RETIRING A ROUTING IS NOT REVERSIBLE — the x-hotelid goes with it, and anything still
                sending that header stops being routed the moment it does. Said plainly, with the
                identifier in the sentence, because a vendor with several looks the same from here. */}
            {confirmRemoveBinding && (
                <ConfirmModal
                    title="Retire this routing"
                    message={`Stop ${vendor.name} reaching `
                        + `${confirmRemoveBinding.hotelname || 'this property'} through `
                        + `${confirmRemoveBinding.system_name}? The x-hotelid `
                        + `${confirmRemoveBinding.uapi_hotel_id} is retired with it, and any call still `
                        + 'sending it is refused immediately.'}
                    confirmLabel="Retire"
                    danger
                    onConfirm={removeBinding}
                    onCancel={() => setConfirmRemoveBinding(null)}
                />
            )}

            {/* Over the call it is testing, opened from that row's Test button.
                TRACE AND JIRA ARE SUPERADMIN'S. Both read /admin/legacy-uapi/*, which a partner admin
                cannot call — a panel that 403s every time would be worse than not offering it. The CALL
                itself is the partner's business and is offered to both. */}
            {tab === 'test' && testOperation && (
                <UapiConsole
                    operation={testOperation}
                    token={token && token.expiresAt > Date.now() ? token : null}
                    gatewayUrl={gatewayUrl}
                    credentialId={id}
                    onAuthenticate={testAuth}
                    // The vendor's OWN bindings, already loaded with the tab. The console's usual source
                    // is an admin endpoint keyed on a property; here the credential is what decides, and
                    // the dispatcher checks exactly this list before it routes anything.
                    // THE CHOSEN ONE, not the list. The property was settled on the tab, so the
                    // console has nothing left to ask: it shows where the call is going and gets on
                    // with the request.
                    bindings={consoleBindings}
                    emptyBindingsMessage={`No property is bound to ${vendor.name}'s credentials, so there `
                        + 'is no x-hotelid to present. Bindings are issued under Legacy › Universal API.'}
                    canTrace={isSuperAdmin}
                    canRaiseIssue={isSuperAdmin}
                    onClose={() => setTestOperation(null)}
                />
            )}
        </div>
    );
};

export default VendorDetailPage;
